"use client"
import type { ItemQuoteInput, TransactionIdentifier } from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import {
  Spinner,
  Toast,
  ToastAvatar,
  ToastContent,
  ToastErrorIcon,
  ToastSide,
  ToastSuccessIcon,
  ToastTitle,
  useToasts,
} from "@opensea/ui-kit"
import { omit } from "es-toolkit"
import { useEffect, useMemo } from "react"
import { useTranslations } from "use-intl"
import { TransactionReceiptFragment } from "../../../components/TransactionReceipt/TransactionReceipt.fragment"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useRenderImage } from "../../../providers/RenderImageProvider"
import { FlowActions } from "../../../stores/flowStore"
import type { FlowStateVariants } from "../../flow-state"

const FETCH_INTERVAL = 500

const query = graphql(
  `
    query MintToastStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
      $swapProvider: SwapProviderType!
    ) {
      buyReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
        swapProvider: $swapProvider
        action: MINT
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
            imageUrl
          }
        }
        failedItemReceipts {
          __typename
        }
        ...TransactionReceipt
      }
    }
  `,
  [TransactionReceiptFragment],
)

type Props = {
  transactionIdentifier?: TransactionIdentifier
  imageUrl: string | null
  isCrossChain: boolean
  itemQuotes: ItemQuoteInput[]
  flow?: FlowStateVariants
}

export function MintToast({
  transactionIdentifier,
  imageUrl,
  itemQuotes,
  isCrossChain = false,
  flow,
}: Props) {
  const t = useTranslations("wallet.MintToast")
  const { renderImage } = useRenderImage()
  const { removeToast } = useToasts()

  const [result, reexecuteQuery] = useQuery({
    query,
    variables: {
      transactionIdentifiers: transactionIdentifier
        ? [transactionIdentifier]
        : [],
      itemQuotes: itemQuotes.map(quote =>
        omit(quote, ["orderId"]),
      ) as ItemQuoteInput[],
      swapProvider: isCrossChain ? "RELAY" : "LOCAL_MINT_NFT",
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
    pause: true,
  })
  const receipt = result.data?.buyReceipt

  useEffect(
    function callOnSuccessWhenComplete() {
      if (receipt?.status === "SUCCESS" && flow) {
        FlowActions.onSuccess(flow)
      }
    },
    [receipt?.status, flow],
  )

  useEffect(
    function pollForReceipt() {
      if (result.fetching) {
        return
      }
      const status = result.data?.buyReceipt.status
      if (status !== undefined && status !== "PENDING") {
        return
      }

      const timerId = setTimeout(() => {
        if (transactionIdentifier || isCrossChain) {
          reexecuteQuery({ requestPolicy: "network-only" })
        }
      }, FETCH_INTERVAL)

      return () => clearTimeout(timerId)
    },
    [
      result.fetching,
      reexecuteQuery,
      result.data?.buyReceipt,
      transactionIdentifier,
      isCrossChain,
    ],
  )

  useEffect(
    function removeToastWhenComplete() {
      let timerId: ReturnType<typeof setTimeout>
      if (receipt && receipt.status !== "PENDING") {
        timerId = setTimeout(removeToast, 1500)
      }

      return () => clearTimeout(timerId)
    },
    [receipt, removeToast],
  )

  const icon = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return <ToastSuccessIcon />
      case "FAILED":
        return <ToastErrorIcon />
      case "PENDING":
      case undefined:
        return <Spinner size="sm" />
    }
  }, [receipt?.status])

  const title = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return t("successTitle")
      case "FAILED":
        return t("failedTitle")
      default:
        return t("pendingTitle")
    }
  }, [receipt?.status, t])

  return (
    <Toast>
      <ToastAvatar renderImage={renderImage} src={imageUrl ?? ""} />
      <ToastContent>
        <ToastTitle>{title}</ToastTitle>
      </ToastContent>
      <ToastSide>{icon}</ToastSide>
    </Toast>
  )
}
